/**
 * NC Intelligence Panel — Table Efficiency extension
 * ===================================================
 * Reads /api/operations/table-efficiency/nc-summary and the drill-down
 * ledger endpoint. Renders as a bento inside Table Efficiency.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Warning, ShieldCheck, TrendUp, ArrowRight, X, Users, Receipt, CurrencyInr } from '@phosphor-icons/react';
import api from '../../lib/api';
import DeltaPill from '../DeltaPill';

const fmtINR = (v) => v == null ? '—' : `₹${Number(v).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const fmtShort = (v) => {
  if (v == null) return '—';
  const n = Number(v);
  if (Math.abs(n) >= 1e7) return `₹${(n/1e7).toFixed(1)}Cr`;
  if (Math.abs(n) >= 1e5) return `₹${(n/1e5).toFixed(2)}L`;
  if (Math.abs(n) >= 1e3) return `₹${(n/1e3).toFixed(1)}K`;
  return `₹${n.toFixed(0)}`;
};
const fmtPct = (v, d=1) => v == null ? '—' : `${Number(v).toFixed(d)}%`;

const VERDICT_STYLE = {
  normal:        { bg: 'bg-emerald-500/[0.04]', border: 'border-emerald-500/30', fg: 'text-emerald-300', icon: ShieldCheck },
  concentrated:  { bg: 'bg-amber-500/[0.05]',   border: 'border-amber-500/30',   fg: 'text-amber-300',   icon: Warning },
  rising:        { bg: 'bg-rose-500/[0.05]',    border: 'border-rose-500/30',    fg: 'text-rose-300',    icon: TrendUp },
  high_frequency:{ bg: 'bg-rose-500/[0.05]',    border: 'border-rose-500/30',    fg: 'text-rose-300',    icon: Warning },
};

const SORT_KEYS = {
  nc_value:      (a, b) => b.nc_value - a.nc_value,
  nc_bills:      (a, b) => b.nc_bills - a.nc_bills,
  nc_covers:     (a, b) => b.nc_covers - a.nc_covers,
  pool_share_pct:(a, b) => b.pool_share_pct - a.pool_share_pct,
  last_nc:       (a, b) => (b.last_nc || '').localeCompare(a.last_nc || ''),
};

export default function NcIntelligencePanel({ outletId, dateFrom, dateTo, onOrderClick }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sortKey, setSortKey] = useState('nc_value');
  const [selectedTable, setSelectedTable] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  useEffect(() => {
    if (!dateFrom || !dateTo) return;
    let alive = true;
    setLoading(true);
    const params = { date_from: dateFrom, date_to: dateTo, include_comparison: true };
    if (outletId) params.outlet_id = outletId;
    api.get('/operations/table-efficiency/nc-summary', { params })
       .then(r => { if (alive) setData(r.data); })
       .catch(() => { if (alive) setData(null); })
       .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [outletId, dateFrom, dateTo]);

  useEffect(() => {
    if (!selectedTable) { setLedger(null); return; }
    let alive = true;
    setLedgerLoading(true);
    const params = { table: selectedTable, date_from: dateFrom, date_to: dateTo, limit: 200 };
    if (outletId) params.outlet_id = outletId;
    api.get('/operations/table-efficiency/nc-ledger', { params })
       .then(r => { if (alive) setLedger(r.data); })
       .catch(() => { if (alive) setLedger(null); })
       .finally(() => alive && setLedgerLoading(false));
    return () => { alive = false; };
  }, [selectedTable, outletId, dateFrom, dateTo]);

  const sorted = useMemo(() => {
    if (!data?.leaderboard) return [];
    return [...data.leaderboard].sort(SORT_KEYS[sortKey] || SORT_KEYS.nc_value);
  }, [data, sortKey]);

  if (loading && !data) {
    return <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 animate-pulse h-64" data-testid="nc-panel-loading" />;
  }
  if (!data || data.totals.nc_bills === 0) {
    return (
      <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5" data-testid="nc-panel-empty">
        <SectionHeader />
        <div className="text-[12px] text-zinc-500 py-8 text-center">No non-chargeable bills in this window.</div>
      </div>
    );
  }

  const t = data.totals;
  const v = data.verdict;
  const vStyle = VERDICT_STYLE[v.status] || VERDICT_STYLE.normal;
  const VerdictIcon = vStyle.icon;
  const changePct = t.nc_value_change_pct;

  return (
    <div className="rounded-xl border border-white/[0.08] bg-[#0c1117]/95 p-5 space-y-4" data-testid="nc-panel">
      <SectionHeader />

      {/* Verdict + Concentration strip */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-3">
        <div className={`rounded-lg border ${vStyle.border} ${vStyle.bg} p-3 flex items-start gap-3`} data-testid="nc-verdict" data-status={v.status}>
          <VerdictIcon size={20} weight="fill" className={vStyle.fg} />
          <div className="min-w-0 flex-1">
            <div className={`text-[10px] uppercase tracking-[0.15em] font-mono font-medium ${vStyle.fg}`}>{v.label}</div>
            <ul className="mt-1 space-y-0.5">
              {v.evidence.map((line, i) => (
                <li key={i} className="text-[12px] text-zinc-200 leading-snug">{line}</li>
              ))}
            </ul>
          </div>
        </div>
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3" data-testid="nc-concentration">
          <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-mono">Concentration</div>
          <div className="mt-1 text-[12px] text-zinc-200 font-mono">{v.concentration_pct}%</div>
          <div className="mt-1 h-1.5 bg-white/[0.05] rounded overflow-hidden">
            <div className="h-full bg-amber-500/70 rounded" style={{ width: `${Math.min(100, v.concentration_pct)}%` }}/>
          </div>
          <div className="mt-1 text-[10px] text-zinc-500">of NC value from top 3 tables</div>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="nc-kpis">
        <Kpi label="NC Value"  value={fmtShort(t.nc_value)}
             delta={changePct != null
               ? <DeltaPill direction={changePct > 0 ? 'up' : changePct < 0 ? 'down' : 'flat'} pct={changePct} testId="nc-value-delta" />
               : (t.previous && t.previous.nc_value === 0 && t.nc_value > 0
                 ? <DeltaPill direction="new" pct={null} testId="nc-value-delta" />
                 : null)}
             sub={t.previous?.nc_value ? `prev ${fmtShort(t.previous.nc_value)}` : (t.previous ? 'no NC prior period' : null)} testid="nc-kpi-value" />
        <Kpi label="NC Bills"  value={t.nc_bills} testid="nc-kpi-bills" />
        <Kpi label="NC Covers" value={t.nc_covers} testid="nc-kpi-covers" />
        <Kpi label="NC Rate"   value={fmtPct(t.nc_rate_pct)}
             sub={`of ${t.eligible_bills} dine-in bills`} testid="nc-kpi-rate" />
      </div>

      {/* Callouts */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-[11px]" data-testid="nc-callouts">
        <CalloutCard title="Most Frequent" data={data.callouts.most_frequent} metric="bills" />
        <CalloutCard title="Highest Value" data={data.callouts.highest_value} metric="value" />
        <CalloutCard title="Largest Avg NC" data={data.callouts.largest_avg}   metric="avg" />
      </div>

      {/* Leaderboard */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-mono">Leaderboard</h4>
          <select value={sortKey} onChange={e=>setSortKey(e.target.value)}
                  className="bg-[#18181B] border border-white/10 rounded px-2 py-1 text-[11px] text-zinc-200 font-mono focus:outline-none focus:border-cyan-500/50"
                  data-testid="nc-leaderboard-sort">
            <option value="nc_value">NC Value</option>
            <option value="nc_bills">NC Bills</option>
            <option value="nc_covers">NC Covers</option>
            <option value="pool_share_pct">Share of Pool</option>
            <option value="last_nc">Most Recent</option>
          </select>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]" data-testid="nc-leaderboard">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
                <th className="text-left py-1.5">Table</th>
                <th className="text-right py-1.5">NC Value</th>
                <th className="text-right py-1.5">Bills</th>
                <th className="text-right py-1.5">Covers</th>
                <th className="text-right py-1.5">Avg</th>
                <th className="text-right py-1.5">Share</th>
                <th className="text-right py-1.5">Last</th>
                <th className="text-right py-1.5">Category</th>
              </tr>
            </thead>
            <tbody>
              {sorted.slice(0, 20).map((r) => (
                <tr key={r.table}
                    onClick={() => setSelectedTable(r.table)}
                    className="border-b border-white/[0.03] hover:bg-white/[0.03] cursor-pointer transition-colors"
                    data-testid={`nc-row-${r.table}`}>
                  <td className="py-2 text-zinc-100 font-medium">{r.table}</td>
                  <td className="py-2 text-right font-mono text-zinc-200">{fmtShort(r.nc_value)}</td>
                  <td className="py-2 text-right font-mono text-zinc-400">{r.nc_bills}</td>
                  <td className="py-2 text-right font-mono text-zinc-400">{r.nc_covers}</td>
                  <td className="py-2 text-right font-mono text-zinc-400">{fmtShort(r.avg_nc_value)}</td>
                  <td className="py-2 text-right font-mono text-amber-300">{fmtPct(r.pool_share_pct)}</td>
                  <td className="py-2 text-right font-mono text-zinc-500 text-[10px]">{r.last_nc || '—'}</td>
                  <td className="py-2 text-right text-zinc-500 text-[10px]">{r.category || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {sorted.length > 20 && (
          <div className="text-[10px] text-zinc-500 mt-2 text-center">Showing top 20 of {sorted.length}</div>
        )}
      </div>

      {/* Category breakdown */}
      {data.categories?.length > 0 && (
        <div>
          <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-mono mb-2">NC by Category</h4>
          <div className="space-y-1" data-testid="nc-categories">
            {data.categories.map(c => {
              const share = t.nc_value > 0 ? (c.value / t.nc_value * 100) : 0;
              return (
                <div key={c.category} className="flex items-center gap-2 text-[11px]" data-testid={`nc-cat-${c.category.replace(/\s+/g,'-')}`}>
                  <span className="text-zinc-300 w-32 truncate">{c.category}</span>
                  <div className="flex-1 h-1.5 bg-white/[0.04] rounded overflow-hidden">
                    <div className="h-full bg-violet-500/70" style={{ width: `${Math.min(100, share)}%` }}/>
                  </div>
                  <span className="font-mono text-zinc-400 w-20 text-right">{fmtShort(c.value)}</span>
                  <span className="font-mono text-zinc-500 w-8 text-right">{c.bills}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Ledger drawer */}
      {selectedTable && (
        <NcLedgerDrawer
          table={selectedTable}
          ledger={ledger}
          loading={ledgerLoading}
          onClose={() => setSelectedTable(null)}
          onOrderClick={onOrderClick}
        />
      )}
    </div>
  );
}

function SectionHeader() {
  return (
    <div className="flex items-baseline justify-between">
      <div>
        <h3 className="text-[13px] font-semibold text-zinc-100">Non-Chargeable Intelligence</h3>
        <p className="text-[11px] text-zinc-500 mt-0.5">
          NC bills, tables and comp patterns. Financial contract preserved (net ₹0, gross in discount pool).
        </p>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, delta, testid }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="mt-1 font-mono text-[18px] text-zinc-100 flex items-baseline gap-1.5">
        <span>{value}</span>{delta}
      </div>
      {sub && <div className="mt-0.5 text-[11px] text-zinc-500">{sub}</div>}
    </div>
  );
}

function CalloutCard({ title, data, metric }) {
  if (!data) return (
    <div className="rounded-lg border border-white/[0.05] bg-white/[0.01] p-3 opacity-50">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
      <div className="text-[11px] text-zinc-600 mt-1">—</div>
    </div>
  );
  const hero =
    metric === 'value' ? fmtShort(data.nc_value) :
    metric === 'avg'   ? fmtShort(data.avg_nc_value) :
    `${data.nc_bills} bills`;
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3" data-testid={`nc-callout-${metric}`}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{title}</div>
      <div className="mt-1 font-mono text-[14px] text-zinc-100 truncate">{data.table}</div>
      <div className="text-[11px] text-zinc-400 font-mono">{hero}</div>
    </div>
  );
}

function NcLedgerDrawer({ table, ledger, loading, onClose, onOrderClick }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="nc-ledger-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-[720px] h-full bg-[#0c1117] border-l border-white/[0.08] overflow-y-auto">
        <div className="sticky top-0 bg-[#0c1117]/95 backdrop-blur border-b border-white/[0.06] px-5 py-3 flex items-center justify-between z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-mono">Table</div>
            <div className="text-[16px] font-semibold text-zinc-100">{table} — NC History</div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100" data-testid="nc-ledger-close">
            <X size={18}/>
          </button>
        </div>

        <div className="p-5 space-y-4">
          {loading && <div className="h-32 rounded-lg bg-white/[0.02] animate-pulse" />}
          {ledger?.hero && (
            <div className="grid grid-cols-4 gap-2" data-testid="nc-ledger-hero">
              <Kpi label="NC Value"  value={fmtShort(ledger.hero.nc_value)} testid="nc-ledger-value" />
              <Kpi label="NC Bills"  value={ledger.hero.nc_bills} testid="nc-ledger-bills" />
              <Kpi label="NC Covers" value={ledger.hero.nc_covers} testid="nc-ledger-covers" />
              <Kpi label="Avg NC"    value={fmtShort(ledger.hero.avg_nc_value)} testid="nc-ledger-avg" />
            </div>
          )}

          {ledger?.timeline?.length > 1 && (
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-mono mb-2">Daily Timeline</h4>
              <div className="space-y-1" data-testid="nc-ledger-timeline">
                {(() => {
                  const max = Math.max(...ledger.timeline.map(t => t.nc_value)) || 1;
                  return ledger.timeline.map(row => (
                    <div key={row.date} className="flex items-center gap-2 text-[11px]">
                      <span className="text-zinc-500 font-mono w-20">{row.date}</span>
                      <div className="flex-1 h-1.5 bg-white/[0.04] rounded overflow-hidden">
                        <div className="h-full bg-rose-500/70" style={{ width: `${(row.nc_value / max) * 100}%` }}/>
                      </div>
                      <span className="font-mono text-zinc-300 w-16 text-right">{fmtShort(row.nc_value)}</span>
                      <span className="font-mono text-zinc-500 w-8 text-right">{row.nc_bills}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}

          {ledger?.bills?.length > 0 ? (
            <div>
              <h4 className="text-[11px] uppercase tracking-[0.15em] text-zinc-400 font-mono mb-2">
                Bills ({ledger.total})
              </h4>
              <table className="w-full text-[11px]" data-testid="nc-ledger-bills">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/[0.06]">
                    <th className="text-left py-1.5">Date</th>
                    <th className="text-left py-1.5">Bill</th>
                    <th className="text-right py-1.5">Gross</th>
                    <th className="text-right py-1.5">Covers</th>
                    <th className="text-left py-1.5 pl-3">Label group</th>
                    <th className="text-left py-1.5">Server</th>
                  </tr>
                </thead>
                <tbody>
                  {ledger.bills.map((b) => (
                    <tr key={b.order_id}
                        onClick={() => onOrderClick && onOrderClick(b.order_id)}
                        className="border-b border-white/[0.03] hover:bg-white/[0.03] cursor-pointer"
                        data-testid={`nc-bill-${b.order_id}`}>
                      <td className="py-1.5 text-zinc-400 font-mono">{b.business_date}</td>
                      <td className="py-1.5 text-cyan-300 font-mono">#{b.order_id}</td>
                      <td className="py-1.5 text-right font-mono text-zinc-200">{fmtINR(b.gross_sales)}</td>
                      <td className="py-1.5 text-right font-mono text-zinc-400">{b.covers}</td>
                      <td className="py-1.5 pl-3 text-zinc-500">{b.label_group}</td>
                      <td className="py-1.5 text-zinc-500">{b.server_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {ledger.total > ledger.bills.length && (
                <div className="text-[10px] text-zinc-500 mt-2 text-center">
                  Showing {ledger.bills.length} of {ledger.total}
                </div>
              )}
            </div>
          ) : (!loading && (
            <div className="text-[11px] text-zinc-500 py-6 text-center">No NC bills for this table.</div>
          ))}
        </div>
      </div>
    </div>
  );
}
